<!--
  One "When <source> <operator> <value> Then <action>" rule row.
-->
<script setup lang="ts">
import {
  CONDITION_OPERATOR_DEFINITIONS,
  CONDITION_SOURCE_DEFINITIONS,
  type ConditionStateField,
  type ConditionalRuleValidationError,
} from "#bb/lib/conditionalRules";
import {
  resolveFieldMeta,
  type AnswerableBlockSummary,
  type BlockSummary,
  type DraftRule,
} from "./ruleModel";
import RuleBlockPicker from "./RuleBlockPicker.vue";
import RuleValueControl from "./RuleValueControl.vue";

const props = withDefaults(
  defineProps<{
    index: number;
    answerableBlocks: AnswerableBlockSummary[];
    allBlocks: BlockSummary[];
    getComponentStateFields: (componentId?: string) => ConditionStateField[];
    actionOptions?: ReadonlyArray<{ value: string; label: string }>;
    showAction?: boolean;
    errors?: ConditionalRuleValidationError[];
  }>(),
  {
    showAction: true,
  },
);
const rule = defineModel<DraftRule>({ required: true });

defineEmits<{ remove: [] }>();

const allBlocksById = computed(() => new Map(props.allBlocks.map((block) => [block.id, block])));

const selectedComponentBlock = computed(() =>
  rule.value.source.blockId
    ? allBlocksById.value.get(rule.value.source.blockId)
    : undefined,
);

const componentStateFieldOptions = computed(() =>
  props.getComponentStateFields(selectedComponentBlock.value?.componentId),
);

const fieldMeta = computed(() =>
  resolveFieldMeta(rule.value.source, {
    answerableBlocks: props.answerableBlocks,
    allBlocksById: allBlocksById.value,
    getComponentStateFields: props.getComponentStateFields,
  }),
);

const operatorOptions = computed(() =>
  CONDITION_OPERATOR_DEFINITIONS.filter((definition) => fieldMeta.value.operators.includes(definition.value)),
);

/** Switching source type starts that source fresh — stale fields from the previous type don't carry over. */
function setSourceType(type: string) {
  rule.value = {
    ...rule.value,
    source: { type: type as DraftRule["source"]["type"] },
    condition: "",
    value: "",
  };
}

function setAnswerBlock(blockId: string) {
  rule.value = {
    ...rule.value,
    source: { type: "answer", blockId },
    condition: "",
    value: "",
  };
}

function setComponentBlock(blockId: string) {
  rule.value = {
    ...rule.value,
    source: { type: "component-state", blockId, field: undefined },
    condition: "",
    value: "",
  };
}

function setComponentField(field: string) {
  rule.value = {
    ...rule.value,
    source: { ...rule.value.source, field },
    condition: "",
    value: "",
  };
}

function patchSource(patch: Partial<DraftRule["source"]>) {
  rule.value = {
    ...rule.value,
    source: { ...rule.value.source, ...patch },
  };
}

function setDataPath(value: string) {
  patchSource({
    dataPath: value
      .split("/")
      .map((segment) => segment.trim())
      .filter(Boolean),
  });
}

function setStateValueType(value: string) {
  rule.value = {
    ...rule.value,
    source: {
      ...rule.value.source,
      valueType: value as "string" | "file" | "number" | "boolean",
    },
    condition: "",
    value: "",
  };
}

function patchRule(patch: Partial<DraftRule>) {
  rule.value = { ...rule.value, ...patch };
}
</script>

<template>
  <div class="space-y-2.5 rounded-md border bg-muted/30 p-3">
    <div class="flex items-center justify-between">
      <span class="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Rule {{ index + 1 }}</span>
      <Button
        variant="ghost"
        size="icon"
        class="size-7 text-muted-foreground hover:text-destructive"
        @click="$emit('remove')"
      >
        <Icon mode="svg" size="15" name="lucide:trash-2" />
      </Button>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <span class="text-[13px] font-medium text-foreground">When</span>

      <Select :model-value="rule.source.type || undefined" @update:model-value="(value) => setSourceType(String(value))">
        <SelectTrigger size="sm" class="h-8 w-[168px] rounded-sm text-[13px]">
          <SelectValue placeholder="Select a source" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="definition in CONDITION_SOURCE_DEFINITIONS" :key="definition.type" :value="definition.type">
            {{ definition.label }}
          </SelectItem>
        </SelectContent>
      </Select>

      <!-- Answer -->
      <RuleBlockPicker
        v-if="rule.source.type === 'answer'"
        :model-value="rule.source.blockId"
        :blocks="answerableBlocks"
        placeholder="Select an answer block"
        empty-label="No answer-capable blocks on this resource yet."
        class="w-[280px]"
        @update:model-value="setAnswerBlock"
      />

      <!-- User state -->
      <template v-else-if="rule.source.type === 'user-state'">
        <Input
          :model-value="rule.source.dataPath?.join('/') ?? ''"
          placeholder="journey/onboarding"
          class="h-8 w-[180px] rounded-sm font-mono text-[13px]"
          @update:model-value="(value) => setDataPath(String(value))"
        />
        <Input
          :model-value="rule.source.key"
          placeholder="assessment.resume"
          class="h-8 w-[140px] rounded-sm font-mono text-[13px]"
          @update:model-value="(value) => patchSource({ key: String(value) })"
        />
        <Select
          :model-value="rule.source.valueType ?? 'string'"
          @update:model-value="(value) => setStateValueType(String(value))"
        >
          <SelectTrigger size="sm" class="h-8 w-[130px] rounded-sm text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="string">String</SelectItem>
            <SelectItem value="file">File path</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
          </SelectContent>
        </Select>
      </template>

      <!-- User metric -->
      <template v-else-if="rule.source.type === 'user-metric'">
        <Input
          :model-value="rule.source.dataPath?.join('/') ?? ''"
          placeholder="engagement"
          class="h-8 w-[180px] rounded-sm font-mono text-[13px]"
          @update:model-value="(value) => setDataPath(String(value))"
        />
        <Input
          :model-value="rule.source.key"
          placeholder="next-clicks"
          class="h-8 w-[150px] rounded-sm font-mono text-[13px]"
          @update:model-value="(value) => patchSource({ key: String(value) })"
        />
      </template>

      <!-- Component state -->
      <template v-else-if="rule.source.type === 'component-state'">
        <RuleBlockPicker
          :model-value="rule.source.blockId"
          :blocks="allBlocks"
          placeholder="Select a component"
          empty-label="No components on this resource yet."
          class="w-[260px]"
          @update:model-value="setComponentBlock"
        />
        <Select
          :model-value="rule.source.field"
          :disabled="!rule.source.blockId"
          @update:model-value="(value) => setComponentField(String(value))"
        >
          <SelectTrigger size="sm" class="h-8 w-[160px] rounded-sm text-[13px]">
            <SelectValue placeholder="Field" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="field in componentStateFieldOptions" :key="field.key" :value="field.key">
              {{ field.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </template>

      <template v-if="rule.source.type">
        <Select :model-value="rule.condition || undefined" @update:model-value="(value) => patchRule({ condition: String(value) })">
          <SelectTrigger size="sm" class="h-8 w-[150px] rounded-sm text-[13px]">
            <SelectValue placeholder="Operator" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="definition in operatorOptions" :key="definition.value" :value="definition.value">
              {{ definition.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <RuleValueControl
          :model-value="rule.value"
          :meta="fieldMeta"
          class="w-[160px]"
          @update:model-value="(value) => patchRule({ value })"
        />
      </template>

      <template v-if="showAction">
        <span class="text-[13px] font-medium text-foreground">Then</span>
        <Select :model-value="rule.action || undefined" @update:model-value="(value) => patchRule({ action: String(value) })">
          <SelectTrigger size="sm" class="h-8 w-[130px] rounded-sm text-[13px]">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in actionOptions ?? []" :key="option.value" :value="option.value">
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </template>
    </div>

    <p
      v-if="errors?.length"
      class="flex items-start gap-1.5 text-[11.5px] text-destructive"
      role="alert"
    >
      <Icon name="lucide:triangle-alert" class="mt-0.5 size-3.5 shrink-0" />
      <span>{{ errors[0].message }}</span>
    </p>
  </div>
</template>
